import {
  useContext,
  useEffect,
  useState,
  useRef,
} from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, TextField } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import SwitchButton from '../../../../Buttons/SwitchButton';
import {
  BlockDataPerPage,
  PagesContext,
  SubPage,
  SubPageOption,
} from '../../../../../contexts/PagesContext';
import ModalComponent from '../../../../ModalComponent';
import SubPageItem from './SubPageItem';
import api from '../../../../../services/api';
import { updateSiteSchema } from '../../../../../validation/schemas/updateSiteSchema';
import SubpageCreateEditModal from '../../../../../pages/My/UserSites/Modals/SubpageCreateEditModal';

type UpdateSiteName = {
  title: string,
};

interface props {
  open: boolean,
  setOpen: (open: boolean) => void,
  pageData: BlockDataPerPage,
  setSubpagesOnParent: (subPages: SubPage[]) => void,
  isSubpage?: boolean,
}

type ModalOperation = 'edit' | 'delete' | 'delete_sitemap' | 'delete_robots' | 'delete_llms';

function URLSettingModal({
  open: configModalOpen,
  setOpen: setConfigModalOpen,
  pageData,
  setSubpagesOnParent: setSubPagesOnParent,
  isSubpage,
}: props) {
  // variables
  const dnsOkStatus = 'OK';
  const dnsNgStatus = 'NG';
  const dnsStatusChangeDomain = 'change_domain';

  const wwwOkStatus = 'OK';
  const wwwNgStatus = 'NG';

  const {
    getSubpageOptions,
    UpdateSiteProperties,
    getPageList,
    contextErrors,
    clearErrors,
  } = useContext(PagesContext);

  const {
    register: addFormValues,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm<UpdateSiteName>({
    mode: 'onSubmit',
    resolver: yupResolver(updateSiteSchema),
    criteriaMode: 'all',
    defaultValues: {
      title: pageData.page_setting.title,
    },
  });

  const switchRef = useRef<HTMLInputElement>(null);
  const initialDomainState = !!pageData.page_setting.custom_url;
  const [siteNameValue, setSiteNameValue] = useState(pageData.page_setting.title);
  const [tmpCustomDomain, setTmpCustomDomain] = useState<string | null>(pageData.page_setting.custom_url);

  const [dnsError, setDnsError] = useState(null);
  const [dnsStatus, setDnsStatus] = useState(null);
  const [checkDnsLoading, setCheckDnsLoading] = useState(false);
  const [isDefaultDomainSwitchEnabled, setIsDefaultDomainSwitchEnabled] = useState(false);
  const [wwwStatus, setWwwStatus] = useState(null);

  const [subPages, setSubPages] = useState(pageData.subpages);
  const [sitemap, setSitemap] = useState(pageData.page_setting.sitemap);
  const [options, setOptions] = useState([] as SubPageOption[]);
  const [selectedSubPage, setSelectedSubPage] = useState<SubPage | null>(null);

  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);

  const [modificationModalOpen, setModificationModalOpen] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalBody, setModalBody] = useState(<></>);
  const [modalConfirmText, setModalConfirmText] = useState('');
  const [modalConfirmColor, setConfirmTextColor] = useState('');
  const [modalOperation, setModalOperation] = useState<ModalOperation>(null);

  const [editingSubpageItemIndex, setEditingSubpageItemIndex] = useState<number | null>(null);
  const [deletingSubpageItemIndex, setDeletingSubpageItemIndex] = useState<number | null>(null);
  const [sitemapFile, setSitemapFile] = useState<File | null>(null);
  const [localErrors, setLocalErrors] = useState<any>({});
  const [isSaving, setIsSaving] = useState(false);

  const [robotsFile, setRobotsFile] = useState<File | null>(null);
  const [robots, setRobots] = useState(pageData.page_setting.robots);

  const [llmsFile, setLlmsFile] = useState<File | null>(null);
  const [llms, setLlms] = useState(pageData.page_setting.llms);

  const setRobotsTxt = (file: File) => {
    if (file !== null) {
      document.getElementById('robots_txt')?.setAttribute('value', file.name);
      setRobotsFile(file);
    } else {
      setRobotsFile(null);
    }
  };

  const setLlmsTxt = (file: File) => {
    document.getElementById('llms_txt')?.setAttribute('value', file.name);
    setLlmsFile(file);
  };

  const setSitemapXml = (file: File) => {
    if (file !== null) {
      document.getElementById('fileselect')?.setAttribute('value', file.name);
      setSitemapFile(file);
    } else {
      setSitemapFile(null);
    }
  };

  const fileSetters = {
    robots: setRobotsTxt,
    llms: setLlmsTxt,
    sitemap: setSitemapXml,
  } as const;

  const isFileValid = (file: File | null | undefined, whichFile: keyof typeof fileSetters) => {
    if (!file) {
      // resets file in case the dude doesnt select one
      fileSetters[whichFile](null);
      return false;
    }

    const validExtensions = (whichFile === 'sitemap') ? ['xml'] : ['txt'];
    const extension = file.name.split('.').pop()?.toLowerCase();
    const errorMessage = `Invalid file format. Valid formats: ${validExtensions.join(', ')}`;

    if (!extension || !validExtensions.includes(extension)) {
      setLocalErrors((prev: any) => ({ ...prev, [whichFile]: errorMessage }));
      return false;
    }

    if (file.size === 0) {
      setLocalErrors((prev: any) => ({ ...prev, [whichFile]: 'FILE TOO SMALL' }));
      return false;
    }

    setLocalErrors((prev: any) => {
      const newErrors = { ...prev };
      delete newErrors[whichFile];
      return newErrors;
    });

    return true;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, whichFile: keyof typeof fileSetters) => {
    const file = e.target.files?.[0];
    if (isFileValid(file, whichFile)) {
      return fileSetters[whichFile](file);
    }
    e.target.value = '';
    return undefined;
  };

  // functions
  const handleDomainSwitchClick = () => {
    setOpenConfirmationModal(true);

    // set custom domain if switch is on
    if (switchRef.current.checked) setTmpCustomDomain(pageData.page_setting.custom_url);
  };

  const handleCustomDomainChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTmpCustomDomain(e.target.value);

    if (e.target.value === pageData.page_setting.custom_url) {
      setDnsStatus(dnsOkStatus);
      return;
    }

    setDnsStatus(dnsStatusChangeDomain);
    setWwwStatus(null); // Reset WWW status when domain changes
  };

  const handleSiteNameChange = (e: React.ChangeEvent<HTMLInputElement>) => setSiteNameValue(e.target.value);

  // #region Add Page
  const handlePageCreation = () => {
    // open add page modal
    setSelectedSubPage({
      id: null,
      url_path: '',
      subpage_id: null,
      subpage_title: '',
      subpage_url: '',
      is_subpage_published: false,
    });

    setModificationModalOpen(true);
  };
  // #endregion

  // #region DNS Status
  function showWWWStatus() {
    const baseStyle = {
      padding: '4px 10px',
      borderRadius: '2px',
      marginBottom: '2px',
    } as React.CSSProperties;

    switch (wwwStatus) {
      case wwwOkStatus:
        return (
          <div style={{ ...baseStyle, background: '#def0db' }}>
            WWW Status:
            <span className="text-bold">Enabled</span>
          </div>
        );
      case wwwNgStatus:
        return (
          <div style={{ ...baseStyle, background: '#fcf1d9' }}>
            WWW Status:
            <span className="text-bold">Disabled</span>
          </div>
        );
      default:
        return (
          <div style={{ ...baseStyle, background: '#fcf1d9' }}>
            WWW Status:
            <span className="text-bold">－</span>
          </div>
        );
    }
  }
  function showDnsStatus() {
    // padding: 4px 10px; border-radius: 2px; margin-bottom: 2px;
    const baseStyle = {
      padding: '4px 10px',
      borderRadius: '2px',
      marginBottom: '2px',
    } as React.CSSProperties;

    switch (dnsStatus) {
      case dnsOkStatus:
        return (
          <div style={{ ...baseStyle, background: '#def0db' }}>
            DNS Status:
            <span className="text-bold">OK</span>
          </div>
        );
      case dnsNgStatus:
        return (
          <div style={{ ...baseStyle, background: '#fcd9db' }}>
            DNS Status:
            <span className="text-bold">NG</span>
          </div>
        );
      default:
        return (
          <div style={{ ...baseStyle, background: '#fcf1d9' }}>
            DNS Status:
            <span className="text-bold">－</span>
          </div>
        );
    }
  }

  const sanitizeDomain = (domain: string): string => {
    let sanitizedDomain = domain.trim();
    sanitizedDomain = sanitizedDomain.replace(/\.+$/, '');
    return sanitizedDomain;
  };

  const checkDomainStatus = async () => {
    setCheckDnsLoading(true);
    setDnsError(null);
    clearErrors();

    // get custom domain
    const domainInput = document.getElementById('domain2') as HTMLInputElement;
    const domain = domainInput.value;

    await api.post(`/v1/web-process/${pageData.id}/check-record`, { domain })
      .then((response) => {
        if (dnsOkStatus === response.data.record_status) {
          setTmpCustomDomain(sanitizeDomain(domain));
        }

        setDnsStatus(response.data.record_status);
        setWwwStatus(response.data.www_status);
      }).catch((err) => {
        setDnsError(err.response.data.message);
      }).finally(() => {
        setCheckDnsLoading(false);
      });
  };
  // #endregion

  // #region Confirmation Modal

  // #region config modal
  const handleSaveConfig = handleSubmit(async (save) => {
    setIsSaving(true);
    UpdateSiteProperties(pageData.id, {
      ...pageData.page_setting,
      title: save.title,
      custom_url: tmpCustomDomain,
      sub_pages: subPages,
      uploaded_sitemap: sitemapFile,
      uploaded_robots: robotsFile,
      uploaded_llms: llmsFile,
      sitemap: sitemapFile ? null : sitemap,
      robots: robotsFile ? null : robots,
      llms: llmsFile ? null : llms,
      www_status: wwwStatus,
    }, () => {
      setSubPagesOnParent(subPages);
      getPageList({});
      setConfigModalOpen(false);
      setIsSaving(false);
    }, () => {
      setConfigModalOpen(true);
      setIsSaving(false);
    });
  });

  const handleCloseConfig = () => {
    reset({ title: pageData.page_setting.title });
    setSiteNameValue(pageData.page_setting.title);
    setTmpCustomDomain(pageData.page_setting.custom_url);
    setIsDefaultDomainSwitchEnabled(!!pageData.page_setting.custom_url);
    setConfigModalOpen(false);
    setDnsError(null);
    clearErrors();
    setIsSaving(false);

    // clear every subpage that has no id
    setSubPages(pageData.subpages);
  };
  // #endregion

  // #region subPage operations
  const handleEditSubPage = (index: number) => {
    // set modal content
    setModalOperation('edit');
    setModalConfirmText('Change');
    setConfirmTextColor('primary');

    setModificationModalOpen(true);
    setEditingSubpageItemIndex(index);
  };

  const handleDeleteSubPage = (subPage: SubPage) => {
    // set modal content
    setModalOperation('delete');
    setModalTitle('Delete page');
    setModalConfirmText('Delete');
    setConfirmTextColor('danger');
    setDeletingSubpageItemIndex(subPages.findIndex((item) => item.id === subPage.id));

    setModalBody(
      <>
        Are you sure you want to delete &quot;
        {subPage.url_path}
        &quot;?
      </>,
    );

    setModalOpen(true);
  };

  const handleSubPageOperationConfirm = () => {
    switch (modalOperation) {
      case 'delete': {
        // close modal
        // delete subpage
        // add deleted flag to subpage
        const newSubPages = subPages?.map((subPage) => {
          if (subPage.id && selectedSubPage && subPage.id === selectedSubPage.id) {
            return {
              ...subPage,
              deleted: true,
            };
          }
          return subPage;
        });

        if (!subPages[deletingSubpageItemIndex].id && deletingSubpageItemIndex !== null) {
          // remove subpage
          newSubPages.splice(deletingSubpageItemIndex, 1);
        }
        setSubPages(newSubPages);
        break;
      }
      case 'delete_sitemap':
        setSitemap({ exists: false, is_deleting: true });
        break;
      case 'delete_robots':
        setRobots({ exists: false, is_deleting: true });
        break;
      case 'delete_llms':
        setLlms({ exists: false, is_deleting: true });
        break;
      case 'edit':
      default:
        setSubPages(subPages);
        break;
    }

    setModalOpen(false);
  };

  const handleModalClose = () => {
    const switchButton = document.getElementById('is_default_domain_enabled') as HTMLInputElement;
    switchButton.click();

    setOpenConfirmationModal(false);
  };

  const handleModalConfirm = () => {
    setIsDefaultDomainSwitchEnabled(!isDefaultDomainSwitchEnabled);
    const swtichButtonState = !isDefaultDomainSwitchEnabled;

    // if the switch is off, clear the custom domain
    if (!swtichButtonState) {
      setTmpCustomDomain(null);
    }

    setOpenConfirmationModal(false);
  };
  // #endregion

  const filterPageOptions = (subpages: SubPage[], subpage_exception_id?: string): SubPageOption[] => options?.filter((option) => {
    if (subpage_exception_id && option.id === subpage_exception_id) {
      return true;
    }

    const filteredSubpages = subpages.filter((subpage) => !subpage.deleted);
    return !filteredSubpages.some((subpage) => subpage.subpage_id === option.id);
  });

  const handleShowDeleteModal = (operation: ModalOperation) => {
    const titles = {
      edit: 'Edit',
      delete: 'Delete',
      delete_sitemap: 'Delete sitemap',
      delete_robots: 'Delete robots.txt',
      delete_llms: 'Delete llms.txt',
    };
    setModalOpen(true);
    setModalTitle(null);
    setConfirmTextColor('danger');
    setModalConfirmText('Delete');
    setModalTitle(titles[operation]);
    setModalBody(
      <div className="p-2">
        Are you sure you want to delete?
        <br />
        The file will be deleted when the site settings are saved.
      </div>,
    );
    setModalOperation(operation);
  };

  // effects
  useEffect(() => {
    const checkMyDns = async () => {
      setCheckDnsLoading(true);

      await api.post(`/v1/web-process/${pageData.id}/check-record`, { domain: tmpCustomDomain })
        .then((response) => {
          setDnsStatus(response.data.record_status);
          setWwwStatus(response.data.www_status);
        }).catch((err) => {
          setDnsError(err.response.data.message);
        }).finally(() => {
          setCheckDnsLoading(false);
        });
    };

    if (tmpCustomDomain) {
      // disable button
      checkMyDns();
      setIsDefaultDomainSwitchEnabled(true);
    } else {
      setDnsStatus(null);
      setWwwStatus(null);
      setIsDefaultDomainSwitchEnabled(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedSubPage) {
      getSubpageOptions(pageData.id, selectedSubPage.subpage_id)
        .then((response) => {
          setOptions(response.data.options);
        });
    }
  }, [getSubpageOptions, pageData.id, selectedSubPage]);

  useEffect(() => {
    setOptions(options);
  }, [options]);

  useEffect(() => {
    setEditingSubpageItemIndex(editingSubpageItemIndex);
  }, [editingSubpageItemIndex]);

  useEffect(() => {
    if (contextErrors?.sitemap) {
      setLocalErrors({ sitemap: contextErrors?.sitemap[0] });
    }
  }, [contextErrors?.sitemap]);

  return (
    <>
      {configModalOpen && (
        <ModalComponent
          show={configModalOpen}
          closeModal={handleCloseConfig}
          title="Site Settings"
          confirmButtonText="Save"
          cancelButtonColor="outline-secondary"
          backdropClassName="modal-backdrop fade show"
          onConfirm={handleSaveConfig}
          onCloseModal={handleCloseConfig}
          backdrop="static"
          disabled={(dnsStatus !== dnsOkStatus) && isDefaultDomainSwitchEnabled}
          loading={isSaving}
          size="lg"
          bodyStyle={{
            maxHeight: 'calc(100vh - 200px)',
            overflowY: 'auto',
          }}
          style={{
            zIndex: 1050,
          }}
        >
          <div className="col-12">
            <label className="col-form-label col-sm-2 text-sm-start text-bold">
              Site Name
              <span>
                *
              </span>
            </label>
            <div>
              <TextField
                {...addFormValues('title')}
                type="text"
                name="title"
                size="small"
                defaultValue={pageData.page_setting.title}
                value={siteNameValue}
                error={!!errors?.title}
                helperText={errors?.title?.message}
                onChange={handleSiteNameChange}
                sx={{ width: '100%' }}
                inputProps={{ translate: 'no', className: 'notranslate' }}
              />
            </div>
            <div className="text-secondary mt-1 ps-2">
              <div className="text-sm">*Used for search results and site management.</div>
            </div>
          </div>

          {
            !isSubpage && (
              <>
                <hr className="mt-4" />

                <div className="text-bold mb-2">Default URL</div>
                <div className="border border-0 p-3 py-2 rounded-3 bg-light mb-2">
                  <div className="text-info">
                    https://
                    {pageData.page_setting.url}
                  </div>
                </div>
                <div className="text-secondary">
                  <div className="text-sm">Unavailable when using a custom domain.</div>
                </div>
                <hr className="mt-4" />
                <div className="d-flex align-content-center justify-content-around w-75 mb-3">
                  <span className="text-bold">
                    Custom Domain Settings
                  </span>

                  <span className="d-flex align-content-center flex-wrap">
                    <SwitchButton
                      id="is_default_domain_enabled"
                      defaultChecked={initialDomainState}
                      onClick={handleDomainSwitchClick}
                      inputRef={switchRef}
                      disabled={pageData.page_setting.is_published}
                    />
                  </span>

                  <span>
                    Use custom domain
                  </span>
                </div>

                {isDefaultDomainSwitchEnabled && (
                  <>
                    <div className="card card-body">
                      <div className="row">
                        <div className="col-12 col-lg-6">
                          <div>
                            <div className="text-bold mb-2">Custom Domain</div>
                            <div className="mb-2 text-sm">
                              <span className="bg-secondary-subtle p-1 me-1 fw-normal">
                                https://
                                <span className="text-info">custom domain string</span>
                                /
                              </span>
                            </div>
                            <div className="mb-1 text-sm">Set your own domain here.</div>
                          </div>
                        </div>
                        <div className="col-12 col-lg-6">
                          <div className="d-flex justify-content-end">
                            <div>
                              {showDnsStatus()}
                              {showWWWStatus()}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="accordion-body">
                        <div className="row my-3">
                          <div className="col-12">
                            {dnsError && (
                              <div className="alert alert-danger">
                                {dnsError}
                              </div>
                            )}
                            <div className="d-flex justify-content-center align-items-center gap-2">
                              <div className="input-group input-group-lg">
                                <label className="input-group-text fw-bold" htmlFor="domain2">https://</label>
                                <input
                                  value={tmpCustomDomain}
                                  onChange={(e) => handleCustomDomainChange(e)}
                                  className="form-control form-control-lg"
                                  type="text"
                                  name="domain2"
                                  id="domain2"
                                  placeholder="yourdomain.com"
                                  readOnly={pageData.page_setting.is_published}
                                />
                                <label className="input-group-text fw-bold bg-white" htmlFor="directory2">/</label>
                              </div>
                              <div>
                                <Button
                                  id="dns-check"
                                  type="button"
                                  className="btn btn-primary btn-urlcheck"
                                  disabled={
                                    checkDnsLoading
                                    || !tmpCustomDomain
                                    || pageData.page_setting.is_published
                                  }
                                  onClick={checkDomainStatus}
                                  style={{ width: '150px' }}
                                >
                                  {checkDnsLoading ? 'Checking...' : 'Check DNS'}
                                </Button>
                              </div>
                            </div>
                            {contextErrors?.custom_url && (
                              <div className="alert alert-danger mt-2">
                                {contextErrors?.custom_url}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="text-secondary">
                          <div className="text-sm">
                            *Update settings at your domain provider:
                            {' '}
                            {process.env.REACT_APP_PROXY_IP ?? 'xxx.xxx.xxx.xxx'}
                          </div>
                          <div className="text-sm">*Enter without &quot;www.&quot; (Example: yourdomain.com)</div>
                          <div className="text-sm">*Allowed: a-z 0-9 &quot;.&quot; &quot;-&quot; &quot;_&quot;</div>
                        </div>
                      </div>

                      {(dnsStatus === dnsOkStatus) && (
                        <div id="sitepagesetting">
                          <div className="mt-4">
                            <div className="row">
                              <div className="col-12">
                                <div className="card">
                                  <div className="card-header">
                                    <div className="d-flex align-items-center gap-1 justify-content-between ">
                                      <div className="text-bold">Site page management</div>
                                      <Button
                                        type="button"
                                        onClick={handlePageCreation}
                                        className="btn btn-primary btn-sm"
                                      >
                                        Add page
                                      </Button>
                                    </div>
                                  </div>
                                  <div className="card-body p-0">
                                    <div className="table-responsive" style={{ minHeight: '170px' }}>
                                      {contextErrors?.sub_pages && (
                                        <div className="alert alert-danger m-2">
                                          {contextErrors?.sub_pages}
                                        </div>
                                      )}

                                      <table className="table table-striped table-hover table-bordered table-nowrap">
                                        <thead>
                                          <tr>
                                            <th style={{ width: '5%' }}>No</th>
                                            <th className="text-center" style={{ width: '10%' }}>Status</th>
                                            <th>URL</th>
                                            <th className="text-center" style={{ width: '120px' }}>Actions</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {/* main-page vvvvvv */}
                                          <tr>
                                            <td className="text-center">-</td>
                                            <td className="text-center">{pageData.page_setting.is_published ? 'Published' : 'Private'}</td>
                                            <td>
                                              <div className="text-md notranslate" translate="no">
                                                https://
                                                {tmpCustomDomain}
                                                /
                                              </div>
                                            </td>
                                            <td className="text-center text-sm">
                                              Not editable
                                              <br />
                                              (Current page)
                                            </td>
                                          </tr>
                                          {/* sub-page vvvvvv */}
                                          {subPages?.filter((subPage) => !subPage.deleted).map((subPage, index) => {
                                            if (!subPage?.deleted) {
                                              return (
                                                <SubPageItem
                                                  key={subPage?.id || index}
                                                  index={index + 1}
                                                  subpage={subPage}
                                                  mainPageDomain={tmpCustomDomain}
                                                  openEditModal={() => handleEditSubPage(index)}
                                                  openDeleteModal={() => handleDeleteSubPage(subPage)}
                                                  setSelectedSubPage={() => setSelectedSubPage(subPage)}
                                                />
                                              );
                                            }

                                            return null;
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="card card-body">
                      {localErrors?.sitemap && (
                        <div className="alert alert-danger">
                          {localErrors?.sitemap}
                        </div>
                      )}
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          Sitemap
                          {pageData.page_setting?.sitemap?.exists && (
                            <span className="ms-1 badge rounded-pill bg-success">Uploaded</span>
                          )}
                        </div>

                        <div>
                          {sitemap?.exists && (
                            <button
                              type="button"
                              className="me-1 btn btn-danger"
                              onClick={() => (handleShowDeleteModal('delete_sitemap'))}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          )}
                          <label className="input-group-btn">
                            {sitemapFile && (
                              <span className="me-2" style={{ color: 'gray' }}>
                                {sitemapFile instanceof File && sitemapFile.name}
                              </span>
                            )}
                            <span className="btn btn-primary text-nowrap">
                              Upload File
                              <input
                                type="file"
                                style={{ display: 'none' }}
                                name="sitemap"
                                onChange={(e) => (handleFileChange(e, 'sitemap'))}
                              />
                            </span>
                          </label>
                        </div>
                      </div>

                      <div className="mt-2 text-end">
                        Click &quot;Update&quot; to apply changes.
                      </div>
                    </div>
                    <div className="card card-body">
                      {localErrors?.robots && (
                        <div className="alert alert-danger">
                          {localErrors?.robots}
                        </div>
                      )}
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          robots.txt
                          {pageData.page_setting?.robots?.exists && (
                            <span className="ms-1 badge rounded-pill bg-success">Uploaded</span>
                          )}
                        </div>
                        <div>

                          {robots?.exists && (
                            <button
                              type="button"
                              className="me-1 btn btn-danger"
                              onClick={() => (handleShowDeleteModal('delete_robots'))}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          )}
                          <label className="input-group-btn">
                            {robotsFile && (
                              <span className="me-2" style={{ color: 'gray' }}>
                                {robotsFile instanceof File && robotsFile.name}
                              </span>
                            )}
                            <span className="btn btn-primary text-nowrap">
                              Upload File
                              <input
                                type="file"
                                style={{ display: 'none' }}
                                name="robots_txt"
                                id="robots_txt"
                                onChange={(e) => handleFileChange(e, 'robots')}
                              />
                            </span>
                          </label>
                        </div>
                      </div>
                      <div className="mt-2 text-end">
                        Click &quot;Update&quot; to apply changes.
                      </div>
                    </div>
                    <div className="card card-body">
                      {localErrors?.llms && (
                        <div className="alert alert-danger">
                          {localErrors?.llms}
                        </div>
                      )}
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          llms.txt
                          {pageData.page_setting?.llms?.exists && (
                            <span className="ms-1 badge rounded-pill bg-success">Uploaded</span>
                          )}
                        </div>
                        <div>
                          {llms?.exists && (
                            <button
                              type="button"
                              className="me-1 btn btn-danger"
                              onClick={() => (handleShowDeleteModal('delete_llms'))}

                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          )}
                          <label className="input-group-btn">
                            {llmsFile && (
                              <span className="me-2" style={{ color: 'gray' }}>
                                {llmsFile instanceof File && llmsFile.name}
                              </span>
                            )}
                            <span className="btn btn-primary text-nowrap">
                              Upload File
                              <input
                                type="file"
                                style={{ display: 'none' }}
                                name="llms_txt"
                                id="llms_txt"
                                onChange={(e) => handleFileChange(e, 'llms')}
                              />
                            </span>
                          </label>
                        </div>
                      </div>
                      <div className="mt-2 text-end">
                        Click &quot;Update&quot; to apply changes.
                      </div>
                    </div>
                  </>
                )}
              </>
            )
          }

        </ModalComponent>
      )}

      <ModalComponent
        show={openConfirmationModal}
        closeModal={handleModalClose}
        onConfirm={handleModalConfirm}
        title="Confirm"
        confirmButtonText={switchRef.current?.checked ? 'Enable' : 'Disable'}
        cancelButtonColor="outline-secondary"
        backdrop="static"
        backdropClassName="modal-backdrop fade show"
        centered
      >
        <div className="m-3">
          <p className="mb-0" id="owndomain-confirm-text">
            {switchRef.current?.checked ? 'Enable custom domain?' : 'Do you want to disable the custom domain?'}
          </p>
        </div>
      </ModalComponent>

      <ModalComponent
        show={modalOpen}
        title={modalTitle}
        onConfirm={handleSubPageOperationConfirm}
        closeModal={() => setModalOpen(false)}
        confirmButtonText={modalConfirmText}
        confirmButtonColor={modalConfirmColor}
        cancelButtonColor="outline-secondary"
        backdrop="static"
        backdropClassName="modal-backdrop fade show"
        centered
      >
        {modalBody}
      </ModalComponent>

      {modificationModalOpen && (
        <SubpageCreateEditModal
          open={modificationModalOpen}
          closeModal={setModificationModalOpen}
          options={filterPageOptions(subPages, selectedSubPage?.subpage_id)}
          subpages={subPages}
          subpage={selectedSubPage}
          domain={tmpCustomDomain}
          setNewSubpage={(subpage: SubPage) => {
            const newSubPages = [...subPages];

            if (editingSubpageItemIndex !== null) {
              newSubPages[editingSubpageItemIndex] = subpage;
            } else {
              // verify if the subapage is 'in trash'
              const inTrash = newSubPages.find((item) => subpage.subpage_id && item.deleted);

              // then update the subpage and remove the 'in trash' flag
              if (inTrash) {
                newSubPages[newSubPages.indexOf(inTrash)] = { ...subpage, id: inTrash.id };
              } else {
                newSubPages.push(subpage);
              }
            }

            setSubPages(newSubPages);
            setEditingSubpageItemIndex(null);
            setSelectedSubPage(null);
          }}
        />
      )}
    </>
  );
}

export default URLSettingModal;
